import React, { useState } from 'react';
import axios from 'axios';
import { CalendarPlus, Check, CalendarDays, Download, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const gFmt = (iso) => {
  const d = new Date(iso);
  return d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
};

export default function AddToCalendar({ event, attending: attendingProp, onChange }) {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const tr = (lv, en, ru) => (language === 'lv' ? lv : language === 'ru' ? ru : en);
  const [attending, setAttending] = useState(!!attendingProp);
  const [busy, setBusy] = useState(false);

  const hasDate = !!event?.event_date;

  const addToKolv = async () => {
    if (!user) { navigate('/login'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/events/${event.id}/attend`, { reminder_minutes: 1440 }, { withCredentials: true });
      setAttending(true);
      onChange?.(true);
      toast.success(tr('Pievienots KoLV kalendāram! Atgādināsim dienu iepriekš.',
        'Added to your KoLV calendar! We will remind you a day before.',
        'Добавлено в календарь KoLV! Напомним за день.'));
    } catch (e) {
      toast.error(tr('Neizdevās pievienot', 'Failed to add', 'Не удалось добавить'));
    } finally { setBusy(false); }
  };

  const removeFromKolv = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/events/${event.id}/attend`, { withCredentials: true });
      setAttending(false);
      onChange?.(false);
      toast(tr('Noņemts no kalendāra', 'Removed from calendar', 'Удалено из календаря'));
    } catch (e) { /* ignore */ } finally { setBusy(false); }
  };

  const googleUrl = () => {
    if (!hasDate) return '#';
    const start = gFmt(event.event_date);
    const end = gFmt(event.end_date || new Date(new Date(event.event_date).getTime() + 2 * 3600 * 1000).toISOString());
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: event.title || '',
      dates: `${start}/${end}`,
      details: event.description || '',
      location: event.location_name || event.address || event.city || event.region || '',
    });
    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          className={attending
            ? 'bg-[#4A6B53] hover:bg-[#3d5944] text-white'
            : 'bg-[#D97736] hover:bg-[#C06528] text-white'}
          disabled={busy}
          data-testid="add-to-calendar-btn"
        >
          {attending ? <Check className="w-4 h-4 mr-1.5" /> : <CalendarPlus className="w-4 h-4 mr-1.5" />}
          {attending ? tr('Kalendārā', 'In calendar', 'В календаре') : tr('Pievienot kalendāram', 'Add to calendar', 'В календарь')}
          <ChevronDown className="w-3.5 h-3.5 ml-1.5 opacity-80" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64" data-testid="add-to-calendar-menu">
        {attending ? (
          <DropdownMenuItem onClick={removeFromKolv} className="text-red-600" data-testid="cal-remove-kolv">
            <Check className="w-4 h-4 mr-2" /> {tr('Noņemt no KoLV kalendāra', 'Remove from KoLV calendar', 'Убрать из календаря KoLV')}
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem onClick={addToKolv} data-testid="cal-add-kolv">
            <CalendarDays className="w-4 h-4 mr-2 text-[#4A6B53]" /> {tr('Pievienot KoLV kalendāram', 'Add to KoLV calendar', 'В календарь KoLV')}
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild disabled={!hasDate} data-testid="cal-google">
          <a href={googleUrl()} target="_blank" rel="noopener noreferrer">
            <CalendarPlus className="w-4 h-4 mr-2 text-[#4285F4]" /> Google Calendar
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem asChild disabled={!hasDate} data-testid="cal-ics">
          <a href={`${API}/events/${event.id}/ics`}>
            <Download className="w-4 h-4 mr-2 text-[#5C5C5C]" /> {tr('.ics (Apple / Outlook)', '.ics (Apple / Outlook)', '.ics (Apple / Outlook)')}
          </a>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
